import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import fetchHook from '../../../hooks/fetchMSSQL-hook'
import { NavigationContext } from '../../../context/navigation-context'
import { LangContext } from '../../../context/lang-context'
import formHook from '../../../hooks/form-hook'
import NoResults from '../../../components/NoResults/NoResults'
import { NotificationManager } from 'react-notifications'
import MachineTypeForm from '../../../components/Modals/MachineTypeForm'
import SearchFilter from "../../../components/Filter/SearchFilter"

const reportList = [
  { reportId: 111, reportColor: 'primary' },
  ["osc", "wyw", "dev"].includes(sessionStorage.getItem("clientId")) && {reportId: 99, reportColor: 'warning'},
].filter(Boolean);

const unwrapRows = (res) => {
  if (Array.isArray(res) && Array.isArray(res[0])) return res[0]
  return Array.isArray(res) ? res : []
}

export default function MachineTypes() {
  const [machineTypes, setMachineTypes] = useState([])
  const [manufacturers, setManufacturers] = useState([])
  const { fetchMssqlApi } = fetchHook()
  const { setHeaderData } = useContext(NavigationContext)
  const { form, openForm, closeForm } = formHook()
  const { TRL_Pack } = useContext(LangContext)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  const [isImporting, setIsImporting] = useState(false)
  const importInputRef = useRef(null)

  const defaultFilter = {
    page: 1,
    rowsPerPage: 100,
    isActive: "true",
    sortBy: "none",
  }

  const [filter, setFilter] = useState(defaultFilter)
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  })

  const rowsPerPage = settingsFilter.rowsPerPage || filter.rowsPerPage || 50

  const sortDefaults = {
    Name: "ASC",
    ManufacturerName: "ASC",
    IsActive: "DESC",
  }

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|")
    return { field, dir: (dir || "").toUpperCase() }
  }

  const sortState = getSortState()

  const arrow = (field) => {
    if (sortState.field !== field) return ""
    return sortState.dir === "ASC" ? " ▲" : " ▼"
  }

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|")
    const curDir = (curDirRaw || "").toUpperCase()

    let nextDir
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC"
    } else {
      nextDir = sortDefaults[field] || "ASC"
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 }
    setFilter(next)
    getMachineTypes(next)
  }

  const getMachineTypes = () => {
    fetchMssqlApi('machine-types', {}, (res) => {
      const rows = unwrapRows(res)
      setMachineTypes(rows)
    })
  }

  const getManufacturers = () => {
    fetchMssqlApi('manufacturers', {}, (res) => {
      setManufacturers(res)
    })
  }

  const getCheckboxFromBool = (bool) => (
    <div className='d-flex justify-content-center align-items-center'>
      <input type='checkbox' checked={!!bool} readOnly />
    </div>
  )

  const getImportErrorMessage = (error) => {
    const data = error?.response?.data || error?.data || error || {}
    const errors = data?.errors || error?.errors

    if (Array.isArray(errors) && errors.length) {
      const visibleErrors = errors.slice(0, 6)

      return (
        <div>
          {visibleErrors.map((err, index) => {
            const row = err.RowNumber ? `Wiersz ${err.RowNumber}` : "Plik"
            const field = err.FieldName ? `, ${err.FieldName}` : ""
            const message = err.Message || err.message || "Błąd walidacji"
            const value = err.Value ? ` (${err.Value})` : ""

            return (
              <div key={index}>
                <strong>{row}</strong>
                {field}: {message}{value}
              </div>
            )
          })}

          {errors.length > visibleErrors.length && (
            <div>
              I jeszcze {errors.length - visibleErrors.length} błędów
            </div>
          )}
        </div>
      )
    }

    return data?.message || error?.message || "Nie udało się zaimportować pliku"
  }

  const finishImport = (input) => {
    if (input) {
      input.value = ""
    }

    setIsImporting(false)
  }

  const handleImportClick = () => {
    if (!isImporting && importInputRef.current) {
      importInputRef.current.click()
    }
  }

  const handleImportChange = (evt) => {
    const input = evt.target
    const file = input.files && input.files[0]

    if (input) {
      input.value = ""
    }

    if (!file || isImporting) return

    const ext = file.name.split(".").pop()?.toLowerCase()

    if (ext !== "xlsx") {
      NotificationManager.error("Wybierz plik .xlsx", "Import")
      return
    }

    const data = new FormData()
    data.append("file", file)

    setIsImporting(true)

    try {
      fetchMssqlApi(
        'machine-types/import',
        { method: "POST", data, hideNotification: true },
        (res) => {
          const updated = res?.UpdatedCount || 0
          const inserted = res?.InsertedCount || 0

          NotificationManager.success(`Zaktualizowano: ${updated}, dodano: ${inserted}`, "Import")
          getMachineTypes()
          finishImport(input)
        },
        (err) => {
          NotificationManager.error(
            getImportErrorMessage(err),
            "Import",
            10000
          )

          finishImport(input)
        }
      )
    } catch (err) {
      NotificationManager.error(
        getImportErrorMessage(err),
        "Import",
        10000
      )

      finishImport(input)
    }
  }

  const formId = useMemo(() => {
    if (!form || form === "new") return null
    const n = Number(form)
    return Number.isFinite(n) ? n : null
  }, [form])

  const selectedMachineType = useMemo(() => {
    if (!formId) return null

    const found = machineTypes.find(
      mt => Number(mt.MachineTypeId) === Number(formId)
    ) || null

    return found
  }, [machineTypes, formId])

  const handleSubmit = (evt, setData) => {
    evt.preventDefault()

    const nameInput = evt.target.elements.name
    const manufacturerIdInput = evt.target.elements.manufacturerId
    if (!nameInput) {
      return
    }

    const nameValue = String(nameInput.value || "").trim()
    const manIdRaw = manufacturerIdInput ? String(manufacturerIdInput.value || "").trim() : ""
    const manId = manIdRaw ? Number(manIdRaw) : null

    const isAvailable = machineTypes
      .filter(mt => Number(mt.MachineTypeId) !== Number(formId))
      .every(mt =>
        String(mt.Name || "").trim().toLowerCase() !== nameValue.toLowerCase()
      )

    if (!isAvailable) {
      NotificationManager.error("Taka nazwa już istnieje")
      setData(prev => ({ ...prev, Name: "" }))
      return
    }

    const payload = {
      Name: nameValue,
      ManufacturerId: manId
    }

    if (form === "new") {
      fetchMssqlApi(
        'machine-type',
        { method: "POST", data: { ...payload, IsActive: true } },
        () => {
          getMachineTypes(filter)
          closeForm()
        }
      )
    } else {
      const current = machineTypes.find(
        mt => Number(mt.MachineTypeId) === Number(formId)
      )

      fetchMssqlApi(
        `machine-type/${formId}`,
        {
          method: "PATCH",
          data: {
            ...payload,
            IsActive: current.IsActive
          }
        },
        () => {
          getMachineTypes(filter)
          setFilter(prev => ({ ...prev, page: 1 }))
          closeForm()
        }
      )
    }
  }

  const filtered = machineTypes.filter(t => {
    if (filter.isActive === "true") return t.IsActive === 1
    if (filter.isActive === "false") return t.IsActive === 0
    return true
  })

  useEffect(() => {
    setHeaderData({ text: "Typy automatów" })
    getMachineTypes()
    getManufacturers()

    const onResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  return (
    <div>
      <SearchFilter
        filterName="machineTypes"
        defaultFilter={defaultFilter}
        reportList={reportList}
        onFilterChange={setFilter}
        onSettingsChange={setSettingsFilter}
        search={getMachineTypes}
        loading={false}
        totalCount={filtered.length}
        dataList={machineTypes}
        forNow
      />

      {filtered.length ? (
        <div className='card'>
          <div className='card-header'>
            <input
              ref={importInputRef}
              type="file"
              accept=".xlsx"
              disabled={isImporting}
              onChange={handleImportChange}
              style={{ display: "none" }}
            />

            <div className="d-flex justify-content-end">
              <button
                className="d-block btn text-nowrap btn-outline-success my-2 mr-2"
                disabled={isImporting}
                onClick={handleImportClick}
              >
                <i className="fas fa-file-import mr-1" />
                {isImporting ? "Import..." : "Import"}
              </button>

              <button
                className="d-block btn text-nowrap btn-outline-secondary my-2 mr-1"
                onClick={openForm("new")}
              >
                <i className="fas fa-plus" />
                {TRL_Pack.locations.addProductButton}
              </button>
            </div>
          </div>

          <div className="overflow-auto custom-scroll" style={{ maxHeight: `${windowHeight - 224}px` }}>
            <table className='table table-striped border w-100'>
              <thead>
                <tr className='headers-stick text-center headers-color'>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("Name")}
                  >
                    Typ{arrow("Name")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("ManufacturerName")}
                  >
                    Producent{arrow("ManufacturerName")}
                  </th>
                  <th
                    style={{ cursor: "pointer", userSelect: "none" }}
                    onClick={() => handleHeaderSort("IsActive")}
                  >
                    Aktywny{arrow("IsActive")}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(
                  (filter.page - 1) * rowsPerPage,
                  filter.page * rowsPerPage
                ).map(machineType => (
                  <tr key={machineType.MachineTypeId}>
                    <td className='text-center'>{machineType.Name}</td>
                    <td className='text-center'>{machineType.ManufacturerName}</td>
                    <td>{getCheckboxFromBool(machineType.IsActive)}</td>
                    <td className='d-flex justify-content-end border-0'>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={openForm(machineType.MachineTypeId)}
                      >
                        Edytuj
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <NoResults
          buttonText={TRL_Pack.locations.addProduct}
          onClick={openForm("new")}
        />
      )}

      {form && (
        <MachineTypeForm
          machineTypeData={form === "new" ? null : selectedMachineType}
          handleClose={closeForm}
          handleSubmit={handleSubmit}
          manufacturers={manufacturers}
        />
      )}
    </div>
  )
}